import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiUrl } from '@/api/config';
import axios from 'axios';

const DiscoveryContext = createContext(null);

export const useDiscovery = () => {
  const context = useContext(DiscoveryContext);
  if (!context) {
    throw new Error('useDiscovery must be used within a DiscoveryProvider');
  }
  return context;
};

export const DiscoveryProvider = ({ children, initialData = null }) => {
  const [promotions, setPromotions] = useState(initialData?.promotions || []);
  const [boards, setBoards] = useState(initialData?.discovery?.boards || []);
  const [grades, setGrades] = useState(initialData?.discovery?.grades || []);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [loadingBoardsGrades, setLoadingBoardsGrades] = useState(false);

  // Sync with initialData changes (hydration)
  useEffect(() => {
    if (initialData) {
      if (initialData.promotions) setPromotions(initialData.promotions);
      if (initialData.discovery?.boards) setBoards(initialData.discovery.boards);
      if (initialData.discovery?.grades) setGrades(initialData.discovery.grades);
    }
  }, [initialData]);

  const refreshPromotions = useCallback(async () => {
    setLoadingPromotions(true);
    try {
      const response = await axios.get(apiUrl('superadmin/promotions/'), {
        params: { platform: 'StudentPortal', is_active: 'true' }
      });
      const data = response.data;
      const list = Array.isArray(data) ? data : (data.results || []);
      setPromotions(list);
      return list;
    } catch (error) {
      console.error('[DiscoveryContext] Failed to refresh promotions:', error);
    } finally {
      setLoadingPromotions(false);
    }
  }, []);

  const refreshBoardsAndGrades = useCallback(async () => {
    setLoadingBoardsGrades(true);
    try {
      const response = await axios.get(apiUrl('superadmin/chapters/boards_and_grades/'));
      const data = response.data;
      if (data.boards) setBoards(data.boards);
      if (data.grades) setGrades(data.grades);
      return data;
    } catch (error) {
      console.error('[DiscoveryContext] Failed to refresh boards and grades:', error);
    } finally {
      setLoadingBoardsGrades(false);
    }
  }, []);

  // Initial load if no initialData was provided
  useEffect(() => {
    if (!initialData) {
      refreshPromotions();
      refreshBoardsAndGrades();
    }
  }, [initialData, refreshPromotions, refreshBoardsAndGrades]);

  const value = {
    promotions,
    boards,
    grades,
    loadingPromotions,
    loadingBoardsGrades,
    refreshPromotions,
    refreshBoardsAndGrades
  };

  return (
    <DiscoveryContext.Provider value={value}>
      {children}
    </DiscoveryContext.Provider>
  );
};
